// ABOUTME: Hook for fetching the overridden scopes list for the ScopeNavigator.
// ABOUTME: Uses React Query to call GET /tuc-configs/{entityId}/scopes.
import {useMemo} from 'react'

import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {ScopeItem, EntityType, getEntityApiPath, apiToLevel} from '../types'

interface SearchScopeItem {
  id?: string
  level: string
  parentId?: string
  countryId: string
  businessLine: string
  displayName: string
  displayNumber?: string
  scopeData?: Record<string, string>
  isOverridden: boolean
}

const LEVEL_ORDER: Record<string, number> = {
  salesOrg: 0,
  dispatchGr: 1,
  plant: 2,
  customer: 3
}

export const useNavigatorScopes = (
  entityType: EntityType | undefined,
  countryId: string | undefined,
  businessLine: string | undefined
): {data: ScopeItem[]; rootScopeId: string | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery({
    queryKey: ['navigatorScopes', entityType, countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<SearchScopeItem[]>(`/tuc-configs/${getEntityApiPath(entityType!)}/scopes`, {
        params: {countryId, businessLine: businessLine!.toUpperCase()}
      })
      return res.data ?? []
    },
    enabled: Boolean(entityType && countryId && businessLine),
    staleTime: 30_000
  })

  const {data, rootScopeId} = useMemo(() => {
    const allItems = rawData ?? []
    const rootItem = allItems.find((item) => item.level === 'Root')
    const nonRootItems = allItems.filter((item) => item.level !== 'Root')
    
    const mappedItems = nonRootItems.map((item) => ({
      id: item.id ?? '',
      displayName: item.displayName,
      displayNumber: item.displayNumber,
      level: apiToLevel(item.level) ?? 'salesOrg',
      isOverridden: item.isOverridden
    })) as ScopeItem[]

    return {
      data: mappedItems,
      rootScopeId: rootItem?.id
    }
  }, [rawData])

  const sorted = useMemo(
    () =>
      (data ?? []).slice().sort((a, b) => {
        const levelDiff = (LEVEL_ORDER[a.level ?? ''] ?? 99) - (LEVEL_ORDER[b.level ?? ''] ?? 99)
        if (levelDiff !== 0) return levelDiff
        return a.displayName.localeCompare(b.displayName, undefined, {sensitivity: 'base'})
      }),
    [data]
  )

  return {data: sorted, rootScopeId, isLoading, error: error as Error | null}
}
